import { useEffect, useRef, useState } from "react";
import type { ReactNode } from "react";
import { Route, Routes, useLocation, useMatch, useNavigate, useParams } from "react-router";
import { useTranslation } from "react-i18next";
import ReactGA from "react-ga4";
import { isEqual } from "lodash";
import * as queryString from "query-string";
import { fromJS } from "immutable";
import { ClientJS } from "clientjs";

import { ActionCreator } from "actions";
import { useAppDispatch, useAppSelector } from "store/hooks";
import { selectIsMobile, selectWindowModals } from "store/selectors/window";
import { selectTheme } from "store/selectors/theme";
import { selectPagesState } from "store/selectors/pages";
import { selectGameActionParams } from "store/selectors/game";
import { selectApplicationShowMenu, selectShowBetHistory } from "store/selectors/application";
import { selectGetBalance, selectGetPages, selectNotificationUrl, selectWlProfile, selectMode } from "store/selectors/links";
import { selectLocale } from "store/selectors/config";
import { selectSessionId } from "store/selectors/security";

// Error and Update stay eager (PLAY-5251 design D7): they show when a request has failed or a
// new deploy is live, which is exactly when a chunk download is most likely to fail. Every other
// container is loaded lazily from `lazyContainers` - never import one statically here (D8).
import Error from "containers/Error";
import Update from "containers/Update";
import { lazyContainers } from "containers/Application/lazyContainers";
import type { LazyContainerName } from "containers/Application/lazyContainers";
import LazyContainer from "containers/Application/LazyBoundary";
import useLatch from "containers/Application/useLatch";
import { OPEN_MODAL_RELS } from "containers/History/constants";

// Suspense fallbacks (D5): the shared loading animation. Navigation and GameNavigation get none -
// they are thin strips, and a loader in each would stack three animations above the content.
import LoadingFallback from "containers/Application/LoadingFallback";
const OVERLAY = <LoadingFallback variant="overlay" />;
const INLINE = <LoadingFallback variant="inline" />;
const PANEL = <LoadingFallback variant="panel" />;

import "containers/Application/styles.scss";
import { getCookie } from "utils/cookie";
import { GamesLayoutType } from "component/common/gamesLayoutType";
import scrollLock from "utils/scrollLock";

// Mirrors the pattern already passed to `withRouterCompat(Application, ...)` at src/index.js
// today (design.md D2) - Application is the only file in the epic reading match.url/.path, both
// of which need an explicit useMatch pattern to compute correctly.
const APPLICATION_ROUTE_PATTERN = "/:locale/:sessionId/*";

// One lazy container inside its boundary and Suspense (PLAY-5251 design D6). `resetKey` is what a
// retry after a failed chunk load waits on: the gate for gated containers, the pathname for
// route-mounted ones.
function lazyElement(name: LazyContainerName, fallback: ReactNode, resetKey: unknown) {
  // Keyed by name: two different containers can share one slot (Categories/Content under the same
  // <Routes>, ShowMore/ShowMoreDesktop), and LazyContainer keeps the component it first loaded,
  // so without a key React would reuse the instance and keep showing the old container.
  return <LazyContainer key={name} name={name} load={lazyContainers[name]} fallback={fallback} resetKey={resetKey} />;
}

// mapStateToProps also returned `skin`, `filtersVisible`, `sizeSuffix`, `configRoute`, `urls`,
// `loginUrl`, and `username` - none read anywhere in this file, dropped rather than ported as
// dead state. mergeProps' `login`, `logout`, `restoreState`, `setWindowScroll`, `onGameClose`,
// `fetchTheme`, and `setTheme` are all similarly dead (`setTheme` appears only inside a
// commented-out block) - dropped too. `handleHistoryManagement`'s body was entirely commented
// out (a confirmed no-op, per this file's own test suite) - dropped along with both of its call
// sites. `noPath` state was set (`match.path === "/"`) but never read anywhere in render - dropped
// as dead state along with the setter call. `componentDidUpdate`'s `if (this.props.nav) ...` read
// a prop nothing ever supplied (not in mapStateToProps, mergeProps, or any ownProp) - dropped.
//
// `pages`/`modals`/`theme` stay Immutable: read with `.get()`/`.getIn()` here, and `theme` is
// passed straight into an inline style via `.toJSON()`.
export function Application() {
  const dispatch = useAppDispatch();
  const { i18n } = useTranslation("translation");
  const navigate = useNavigate();
  const location = useLocation();
  const params = useParams();
  // Of the 3 original match.url/.path reads, only the 2 match.url ones survive: the third
  // (match.path === "/") only ever fed `noPath` state that render() never read - dropped as dead
  // code along with that state, so match.path itself is no longer read.
  const routeMatch = useMatch(APPLICATION_ROUTE_PATTERN);
  const matchUrl = routeMatch?.pathnameBase;

  const isMobile = useAppSelector(selectIsMobile);
  const modals = useAppSelector(selectWindowModals);
  const theme = useAppSelector(selectTheme);
  const pages = useAppSelector(selectPagesState);
  const gameActionParams = useAppSelector(selectGameActionParams);
  const applicationShowMenu = useAppSelector(selectApplicationShowMenu);
  const getBalance = useAppSelector(selectGetBalance);
  const getPagesLink = useAppSelector(selectGetPages);
  const notificationUrl = useAppSelector(selectNotificationUrl);
  const wlProfile = useAppSelector(selectWlProfile);
  const mode = useAppSelector(selectMode);
  const locale = useAppSelector(selectLocale);
  const sessionId = useAppSelector(selectSessionId);
  const showBetHistory = useAppSelector(selectShowBetHistory);
  const appEnv = window.APP_ENV;

  const qs = queryString.parse(window.location.search);
  const {
    games,
    showMore,
    modalRel,
    showTournamentDetails,
    showTournamentResults,
    tournamentDetails,
    route,
    showJackpotDetails,
    gameId,
    playType,
    q,
  } = qs;
  const hasModalLaunched =
    games ||
    showMore ||
    modalRel ||
    gameActionParams ||
    showTournamentDetails ||
    route ||
    showJackpotDetails ||
    applicationShowMenu;

  // Lazy-mount gates (PLAY-5251 design D2/D3). Each is true whenever its container would render
  // something, and may be true more often - the container's own `return null` stays the
  // authority. Deliberately looser than the container's check:
  //   - ShowMore/ShowMoreDesktop, TournamentViewDetailsDesktop, TournamentLeaderboard,
  //     JackpotViewDetails: the container also needs sessionId.
  //   - FreeRounds: "route present"; the container also decodes it and needs rel rewards.details.
  //   - JackpotDetails: "tournamentDetails present"; the container also resolves a
  //     jackpot.details link from state.games.
  //   - BetHistory: the container also needs its service.url profile link.
  //   - GameAction: isMobile only, so on mobile it mounts at startup as before and its handover
  //     from GameActionCollapse keeps working; its chunk is never fetched on desktop (D2).
  const gate = {
    search: q !== undefined,
    gameActionCollapse: !!gameActionParams,
    showMore: !!showMore,
    games: !!games,
    tournamentViewDetails: !!showTournamentDetails,
    freeRounds: !!route,
    tournamentLeaderboard: !!showTournamentResults,
    jackpotDetails: !!tournamentDetails,
    jackpotViewDetails: !!showJackpotDetails,
    gameAction: !!isMobile,
    menu: !!applicationShowMenu,
    history: typeof modalRel === "string" && OPEN_MODAL_RELS.includes(modalRel),
    roundHistoryPerGame: modalRel === "round-history-per-game.links",
    gameRoundResult: modalRel === "game-round-result",
    betHistory: !!showBetHistory,
  };

  // Each gate is latched: once open, the container stays mounted so its close effects still run.
  // The fallback follows the live gate, so a closed container never shows a placeholder.
  const mounted = {
    search: useLatch(gate.search),
    gameActionCollapse: useLatch(gate.gameActionCollapse),
    showMore: useLatch(gate.showMore),
    games: useLatch(gate.games),
    tournamentViewDetails: useLatch(gate.tournamentViewDetails),
    freeRounds: useLatch(gate.freeRounds),
    tournamentLeaderboard: useLatch(gate.tournamentLeaderboard),
    jackpotDetails: useLatch(gate.jackpotDetails),
    jackpotViewDetails: useLatch(gate.jackpotViewDetails),
    gameAction: useLatch(gate.gameAction),
    menu: useLatch(gate.menu),
    history: useLatch(gate.history),
    roundHistoryPerGame: useLatch(gate.roundHistoryPerGame),
    gameRoundResult: useLatch(gate.gameRoundResult),
    betHistory: useLatch(gate.betHistory),
  };

  function gated(key: keyof typeof gate, name: LazyContainerName, fallback: ReactNode) {
    if (!mounted[key]) return null;
    return lazyElement(name, gate[key] ? fallback : null, gate[key]);
  }

  const { pathname } = location;

  const [scrollY, setScrollY] = useState(0);
  const mainContentRef = useRef<HTMLDivElement | null>(null);

  function onWindowResize() {
    const height = window.innerHeight || document.documentElement.clientHeight || document.body.clientHeight;
    const width = window.innerWidth || document.documentElement.clientWidth || document.body.clientWidth;
    dispatch(ActionCreator.setWindowSize({ height, width }) as any);
  }

  async function supportsAVIF(): Promise<boolean> {
    return new Promise((resolve) => {
      const avif = new Image();
      avif.src =
        "data:image/avif;base64,AAAAIGZ0eXBhdmlmAAAAAGF2aWZtaWYxbWlhZk1BMUIAAADybWV0YQAAAAAAAAAoaGRscgAAAAAAAAAAcGljdAAAAAAAAAAAAAAAAGxpYmF2aWYAAAAADnBpdG0AAAAAAAEAAAAeaWxvYwAAAABEAAABAAEAAAABAAABGgAAAB0AAAAoaWluZgAAAAAAAQAAABppbmZlAgAAAAABAABhdjAxQ29sb3IAAAAAamlwcnAAAABLaXBjbwAAABRpc3BlAAAAAAAAAAIAAAACAAAAEHBpeGkAAAAAAwgICAAAAAxhdjFDgQ0MAAAAABNjb2xybmNseAACAAIAAYAAAAAXaXBtYQAAAAAAAAABAAEEAQKDBAAAACVtZGF0EgAKCBgANogQEAwgMg8f8D///8WfhwB8+ErK42A=";
      avif.onload = () => resolve(true);
      avif.onerror = () => resolve(false);
    });
  }

  // Dead: componentDidMount calls supportsAVIF() twice instead of calling this (documented,
  // pre-existing source bug left unfixed - test-only phase).
  async function supportsWebp(): Promise<boolean> {
    return new Promise((resolve) => {
      const webp = new Image();
      webp.src = "data:image/webp;base64,UklGRiIAAABXRUJQVlA4TAYAAAAvAAAAAAfQ//73v/+BiOh/AAA=";
      webp.onload = () => resolve(true);
      webp.onerror = () => resolve(false);
    });
  }

  useEffect(() => {
    (async () => {
      if (!pages) {
        scrollLock.lock(mainContentRef.current);
      }

      const isAvifSupported = await supportsAVIF();
      const isWebpSupported = await supportsAVIF();
      const imageFormat: any = {
        banner: "jpeg",
        icon: "png",
        gamelobbyBanner: "jpeg",
        tournaments: "jpeg",
        providers: "jpeg",
      };

      if (isAvifSupported) {
        imageFormat.banner = "avif";
        imageFormat.icon = "avif";
        imageFormat.gamelobbyBanner = "avif";
        imageFormat.tournaments = "avif";
        imageFormat.logoRound = "avif";
        imageFormat.providers = "avif";
      } else if (isWebpSupported) {
        imageFormat.banner = "webp";
        imageFormat.icon = "webp";
        imageFormat.gamelobbyBanner = "webp";
        imageFormat.tournaments = "webp";
        imageFormat.logoRound = "webp";
        imageFormat.providers = "webp";
      }

      if (hasModalLaunched) {
        document.getElementById("root")!.classList.add("has-modal");
        document.body.classList.add("has-modal");
      } else {
        document.getElementById("root")!.classList.remove("has-modal");
        document.body.classList.remove("has-modal");
      }

      onWindowResize();
      window.addEventListener("resize", onWindowResize);

      const client = new ClientJS();

      if (client.getBrowserData().os.name.toLowerCase() === "android") {
        document.documentElement.classList.add("android");
      }

      let hideSplash: boolean | undefined;
      let showMenu: boolean | undefined;
      let appId: string | undefined;

      if (location.search) {
        const urlParams = new URLSearchParams(location.search);
        hideSplash = urlParams.get("hideSplash") ? urlParams.get("hideSplash") === "true" : undefined;
        showMenu = urlParams.get("showMenu") ? urlParams.get("showMenu") === "true" : undefined;
        appId = urlParams.get("appId") ?? undefined;
      }

      const inIframe = () => {
        try {
          return window.self !== window.top;
        } catch (e) {
          return true;
        }
      };
      const isTouch = "ontouchstart" in document.documentElement;
      const browserProps = {
        browser: navigator.userAgent,
        isMobile: client.isMobile() || client.getBrowser() === "Mobile Safari" || isTouch,
        isMobileIOS: client.isMobileIOS(),
        hideSplash,
        showMenu,
        appId,
        inIframe: inIframe(),
        imageFormat: fromJS(imageFormat),
      };

      let srvcred: any = getCookie("srvcred");
      srvcred = srvcred === "" ? {} : JSON.parse(srvcred);

      if (!srvcred[params.sessionId as string]) {
        srvcred[params.sessionId as string] = {
          dateCreated: new Date(),
          uuid: null,
          gs: null,
        };
      }

      const cookieProps = {
        sessionId: params.sessionId,
        hideSplash,
        showMenu,
        srvcred: JSON.stringify(srvcred),
      };

      const { uuid, gs } = srvcred[params.sessionId as string];
      const initParams = { ...params, uuid, gs };

      dispatch(ActionCreator.setBrowser(browserProps) as any);
      dispatch(ActionCreator.initApp(initParams) as any);
      dispatch(ActionCreator.setCookie(cookieProps) as any);

      if (window.APP_ENV) {
        console.log("APP_ENV", window.APP_ENV);
        let measurementId;

        switch (window.APP_ENV) {
          case "production":
            measurementId = "G-0WSCKQDVMJ";
            break;
          default:
            measurementId = "G-YLRXB8S8G1";
            break;
        }

        ReactGA.initialize(measurementId, { debug: true } as any);
      }
    })();

    return () => {
      window.removeEventListener("resize", onWindowResize);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isFirstRenderRef = useRef(true);
  const prevRef = useRef({
    pages,
    hasModalLaunched,
    locale,
    appEnv,
    mode,
    getBalance,
    getPagesLink,
    notificationUrl,
    wlProfile,
    sessionId,
    modals,
    location,
  });

  // Mirrors UNSAFE_componentWillReceiveProps + componentDidUpdate, both of which never fire on
  // mount - only react once a value changes on a later render. Runs on every render (no
  // dependency array), same as the class comparing props on every update, and guards itself via
  // the per-field checks below.
  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      prevRef.current = {
        pages,
        hasModalLaunched,
        locale,
        appEnv,
        mode,
        getBalance,
        getPagesLink,
        notificationUrl,
        wlProfile,
        sessionId,
        modals,
        location,
      };
      return;
    }

    const prev = prevRef.current;
    prevRef.current = {
      pages,
      hasModalLaunched,
      locale,
      appEnv,
      mode,
      getBalance,
      getPagesLink,
      notificationUrl,
      wlProfile,
      sessionId,
      modals,
      location,
    };

    if (!prev.pages && pages) {
      scrollLock.unlock(mainContentRef.current);
    }

    if (prev.pages && !pages) {
      scrollLock.lock(mainContentRef.current);
    }

    if (hasModalLaunched !== prev.hasModalLaunched) {
      if (hasModalLaunched) {
        document.getElementById("root")!.classList.add("has-modal");
        document.body.classList.add("has-modal");
      } else {
        document.getElementById("root")!.classList.remove("has-modal");
        document.body.classList.remove("has-modal");
      }
    }

    if (locale !== prev.locale && locale) {
      i18n.changeLanguage(locale);
    }

    if (appEnv !== prev.appEnv && appEnv) {
      console.log("appEnv:", appEnv);
      let measurementId;

      switch (appEnv) {
        case "production":
          measurementId = "G-0WSCKQDVMJ";
          break;
        default:
          measurementId = "G-YLRXB8S8G1";
          break;
      }

      ReactGA.initialize(measurementId);
    }

    if (mode && mode !== prev.mode) {
      ReactGA.event({
        action: "load_app",
        category: mode,
      });
    }

    if (getBalance && getBalance !== prev.getBalance) {
      dispatch(ActionCreator.fetchBalance() as any);
    }

    if (getPagesLink && getPagesLink !== prev.getPagesLink) {
      dispatch(ActionCreator.wlFetchPages() as any);
    }

    if (notificationUrl && notificationUrl !== prev.notificationUrl) {
      dispatch(ActionCreator.initServerSent(notificationUrl) as any);
    }

    if (wlProfile && wlProfile !== prev.wlProfile) {
      dispatch(ActionCreator.wlFetchProfile() as any);
    }

    if (sessionId && sessionId !== prev.sessionId) {
      dispatch(ActionCreator.fetchLinks() as any)
        .then((response: any) => {
          const { gs, uuid } = response;
          const payload = { gs, uuid };
          dispatch(ActionCreator.setCookie(payload) as any);
          return response;
        })
        .then((response: any) => {
          dispatch(ActionCreator.setLinks(response) as any);
        });
    }

    if (modals && modals !== prev.modals) {
      if (modals.size > 0) {
        setScrollY(window.pageYOffset);
      } else {
        window.scrollTo(0, scrollY);
      }
    }

    if (isEqual(location, prev.location)) {
      const search: any = queryString.parse(location.search);
      if (search.nav && search.playType) {
        if (search.gameId) {
          ReactGA.event({ category: search.nav, action: search.playType, label: search.gameId });
          if (search.q) {
            ReactGA.event({ action: "launch_on_search", category: search.nav, label: search.q });
          }
        }
        if (search.gameActionId) {
          ReactGA.event({ category: search.nav, action: search.playType, label: search.gameActionId });
          if (search.q) {
            ReactGA.event({ action: "launch_on_search", category: search.nav, label: search.q });
          }
        }
      }
    }

    if (pages && !isEqual(pages, prev.pages)) {
      let newLoc = location.pathname;

      if (matchUrl === location.pathname) {
        const { locale: paramLocale, sessionId: paramSessionId, page, category } = params;

        const myQT = pages.get("wl-pages").find((p: any) => p.get("rel") === "myQT");
        const webMainLobby = pages.get("wl-pages").find((p: any) => p.get("rel") === "webLobbyMain");
        const initPage = webMainLobby.getIn(["content", 0]);

        if (myQT) {
          newLoc = `/${paramLocale}/${paramSessionId}/categories/${myQT.get("rel")}`;
        } else if (!page || !category) {
          newLoc = `/${paramLocale}/${paramSessionId}/games/${initPage.get("rel")}/${initPage
            .getIn(["links"])
            .find((link: any) => link.get("rel").indexOf("games") > -1)
            .get("rel")}`;
        }
      }

      if (gameId && !(isMobile && playType === "game.details")) {
        let gamesList = fromJS([null, null, null, null]);
        const gameParam: any = new URLSearchParams();
        gameParam.gameId = gameId;
        if (playType) gameParam.playType = playType;

        gamesList = gamesList.setIn([0], gameParam);

        const gamesEnc = btoa(encodeURIComponent(JSON.stringify(gamesList.toJSON())));

        const navParams = {
          games: gamesEnc,
          layout: GamesLayoutType.LAYOUT_1,
        };

        newLoc = `${newLoc}?${queryString.stringify(navParams)}`;
      } else {
        newLoc = `${newLoc}${location.search}`;
      }

      if (newLoc !== matchUrl) {
        navigate(newLoc);
      }
    }
  });

  return (
    <div className="container-fluid application nopadding" style={theme ? theme.toJSON() : {}}>
      <>
        <div ref={mainContentRef} className="main-content d-flex flex-column">
          <Routes>
            <Route path="games/:page/*" element={lazyElement("Navigation", null, pathname)} />
            <Route path="categories/:page/*" element={lazyElement("Navigation", null, pathname)} />
          </Routes>
          <Routes>
            <Route path="games/:page/*" element={lazyElement("GameNavigation", null, pathname)} />
            <Route
              path="categories/:page/*"
              element={lazyElement("GameNavigation", null, pathname)}
            />
          </Routes>
          <Routes>
            <Route path="games/:page/:category/*" element={lazyElement("Content", INLINE, pathname)} />
            <Route path="categories/:page/*" element={lazyElement("Categories", INLINE, pathname)} />
          </Routes>
          <Routes>
            <Route path="*" element={gated("search", "Search", OVERLAY)} />
          </Routes>
          <Routes>
            <Route
              path="*"
              element={
                isMobile
                  ? gated("showMore", "ShowMore", OVERLAY)
                  : gated("showMore", "ShowMoreDesktop", OVERLAY)
              }
            />
          </Routes>
          <Routes>
            <Route path="*" element={gated("games", "Games", OVERLAY)} />
          </Routes>
          <Routes>
            <Route
              path="*"
              element={gated(
                "tournamentViewDetails",
                "TournamentViewDetailsDesktop",
                OVERLAY,
              )}
            />
          </Routes>
          <Routes>
            <Route path="*" element={gated("freeRounds", "FreeRounds", OVERLAY)} />
          </Routes>
          <Routes>
            <Route
              path="*"
              element={gated("tournamentLeaderboard", "TournamentLeaderboard", OVERLAY)}
            />
          </Routes>
          <Routes>
            <Route path="*" element={gated("jackpotDetails", "JackpotDetails", OVERLAY)} />
          </Routes>
          <Routes>
            <Route
              path="*"
              element={gated("jackpotViewDetails", "JackpotViewDetails", OVERLAY)}
            />
          </Routes>
          <Routes>
            {/* Gated on isMobile, so the loader must not show for a closed GameAction: its own
                condition (gameId && playType) has to hold too. */}
            <Route
              path="*"
              element={gated("gameAction", "GameAction", gameId && playType ? OVERLAY : null)}
            />
          </Routes>
          <Routes>
            <Route
              path="*"
              element={gated("gameActionCollapse", "GameActionCollapse", OVERLAY)}
            />
          </Routes>
          <Routes>
            {/* The hamburger trigger lives in Navigation, and a closed Menu renders nothing, so
                Menu mounts on first open and then stays latched. */}
            <Route path="*" element={gated("menu", "Menu", PANEL)} />
          </Routes>
          <Routes>
            <Route path="*" element={gated("history", "History", OVERLAY)} />
          </Routes>
          <Routes>
            <Route
              path="*"
              element={gated("roundHistoryPerGame", "RoundHistoryPerGame", OVERLAY)}
            />
          </Routes>
          <Routes>
            <Route path="*" element={gated("gameRoundResult", "GameRoundResult", OVERLAY)} />
          </Routes>
          <Routes>
            <Route path="*" element={gated("betHistory", "BetHistory", OVERLAY)} />
          </Routes>
          <Error />
          <Routes>
            <Route path="*" element={<Update />} />
          </Routes>
          <Routes>
            {/* Exit only runs a redirect and draws nothing, so it has no fallback. */}
            <Route path="exit/*" element={lazyElement("Exit", null, pathname)} />
          </Routes>
          <div className="d-none" />
        </div>
      </>
    </div>
  );
}

export default Application;
